const React = require('react');
const { default: Box } = require('@mui/material/Box');
const { default: ButtonBase } = require('@mui/material/ButtonBase');
const { default: TextField } = require('@mui/material/TextField');
const { default: Styled } = require('styled-components');
const { HexColorPicker } = require('react-colorful');
const FieldContext = require('./FieldContext');
const { PAGE_RELOAD_PREPARE_EVENT } = require('utils/reload');
const { useCallback, useEffect, useMemo, useRef, useState } = React;

const internals = {};

internals.DEFAULT_COLOR = '#ffffff';

internals.PRESET_COLORS = [
    '#111827', '#374151', '#6b7280', '#9ca3af', '#ffffff',
    '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
    '#22c55e', '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6',
    '#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899'
];

internals.isValidHexColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value || '').trim());

internals.normalizeHexColor = (value) => {

    const text = String(value || '').trim();

    if (/^[0-9a-f]{6}$/i.test(text)) {
        return `#${text}`;
    }

    if (internals.isValidHexColor(text)) {
        return text;
    }

    return '';
};

internals.getPreviewColor = (value) => internals.normalizeHexColor(value) || internals.DEFAULT_COLOR;

module.exports = function ColorField(props) {

    const {
        field = {},
        fieldName = field.name,
        value,
        setValue,
        textFieldProps = {}
    } = props;

    const fieldController = FieldContext.useField(fieldName);
    const resolvedValue = typeof value === 'undefined' ? fieldController.getValue() || '' : value;

    const setFieldValue = typeof setValue === 'function' ? setValue : fieldController.setValue;

    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const rootRef = useRef(null);
    const previewColor = useMemo(() => internals.getPreviewColor(resolvedValue), [resolvedValue]);

    const closePicker = useCallback(() => {

        setIsPickerOpen(false);
    }, []);

    const handleColorChange = useCallback((hex, options = {}) => {

        setFieldValue(hex);
        if (options.closePicker) {
            closePicker();
        }
    }, [closePicker, setFieldValue]);

    useEffect(() => {

        if (!isPickerOpen) {
            return undefined;
        }

        const handleDocumentMouseDown = (event) => {

            if (rootRef.current && !rootRef.current.contains(event.target)) {
                closePicker();
            }
        };

        document.addEventListener('mousedown', handleDocumentMouseDown);

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown);
        };
    }, [closePicker, isPickerOpen]);

    useEffect(() => {

        window.addEventListener(PAGE_RELOAD_PREPARE_EVENT, closePicker);
        window.addEventListener('pagehide', closePicker);
        const visibilityHandler = () => { if (document.visibilityState === 'hidden') closePicker(); };
        window.addEventListener('visibilitychange', visibilityHandler);

        return () => {
            window.removeEventListener(PAGE_RELOAD_PREPARE_EVENT, closePicker);
            window.removeEventListener('pagehide', closePicker);
            window.removeEventListener('visibilitychange', visibilityHandler);
        };
    }, [closePicker]);

    return (
        <Box ref={rootRef} sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', width: '100%' }}>
            <SwatchButton
                aria-label={`Choose ${field.label || fieldName}`}
                aria-haspopup='dialog'
                aria-expanded={isPickerOpen}
                onClick={() => setIsPickerOpen((prev) => !prev)}
                type='button'
                $color={previewColor}
            />
            <StyledTextField
                fullWidth
                label={field.label}
                value={resolvedValue || ''}
                onChange={(event) => setFieldValue(event.target.value)}
                {...textFieldProps}
                inputProps={{ ...(textFieldProps.inputProps || {}), type: 'text' }}
            />
            {isPickerOpen && (
                <PalettePanel role='dialog' aria-label={`${field.label || fieldName} color palette`}>
                    <StyledHexColorPicker
                        color={previewColor}
                        onChange={handleColorChange}
                    />
                    <PickerHexField
                        label='Custom hex'
                        size='small'
                        value={resolvedValue || ''}
                        onChange={(event) => setFieldValue(event.target.value)}
                        inputProps={{ type: 'text' }}
                    />
                    <PresetGrid aria-label='Preset colors'>
                        {internals.PRESET_COLORS.map((hex) => (
                            <PaletteSwatch
                                key={hex}
                                aria-label={hex}
                                onClick={() => handleColorChange(hex, { closePicker: true })}
                                type='button'
                                $color={hex}
                                $selected={internals.normalizeHexColor(resolvedValue).toLowerCase() === hex}
                            />
                        ))}
                    </PresetGrid>
                </PalettePanel>
            )}
        </Box>
    );
};

const SwatchButton = Styled(ButtonBase)`
    width: 40px;
    height: 40px;
    border: 1px solid rgba(17, 24, 39, 0.22) !important;
    border-radius: 6px !important;
    background:
        linear-gradient(45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(-45deg, #d1d5db 25%, transparent 25%),
        linear-gradient(45deg, transparent 75%, #d1d5db 75%),
        linear-gradient(-45deg, transparent 75%, #d1d5db 75%);
    background-color: #ffffff;
    background-position: 0 0, 0 8px, 8px -8px, -8px 0;
    background-size: 16px 16px;
    flex: 0 0 40px;
    overflow: hidden;

    &:after {
        background: ${({ $color }) => $color};
        border: ${({ $color }) => $color.toLowerCase() === '#ffffff' ? '1px solid rgba(17, 24, 39, 0.14)' : '0'};
        content: '';
        inset: 3px;
        position: absolute;
        border-radius: 4px;
    }
`;

const PalettePanel = Styled.div`
    background: #ffffff;
    border: 1px solid rgba(17, 24, 39, 0.12);
    border-radius: 8px;
    box-shadow: 0 12px 32px rgba(17, 24, 39, 0.18);
    left: 0;
    padding: 12px;
    position: absolute;
    top: 46px;
    width: 240px;
    z-index: 20;
`;

const StyledHexColorPicker = Styled(HexColorPicker)`
    width: 100% !important;

    .react-colorful__saturation {
        border-radius: 6px 6px 0 0;
    }

    .react-colorful__hue {
        border-radius: 0 0 6px 6px;
        height: 18px;
    }

    .react-colorful__pointer {
        width: 18px;
        height: 18px;
    }
`;

const PickerHexField = Styled(TextField)`
    margin-top: 12px !important;
    width: 100%;
`;

const PresetGrid = Styled.div`
    display: grid;
    grid-template-columns: repeat(5, 32px);
    gap: 8px;
    margin-top: 12px;
`;

const PaletteSwatch = Styled.button`
    width: 32px;
    height: 32px;
    border: ${({ $selected }) => $selected ? '2px solid #111827' : '1px solid rgba(17, 24, 39, 0.18)'};
    border-radius: 6px;
    background: ${({ $color }) => $color};
    box-shadow: ${({ $selected }) => $selected ? '0 0 0 2px rgba(17, 24, 39, 0.12)' : 'none'};
    cursor: pointer;
    padding: 0;

    &:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
    }
`;

const StyledTextField = Styled(TextField)`
`;

module.exports.internals = internals;